import { ContributionMarginYearBreakdown } from "models";

import { bandAvg } from "pages/app/scenario-analysis/utils/minAvgMax";

export type OnboardingForecastPoint = {
  yearNum: number;
  baselineCumulative: number;
  changeCumulative: number | null;
  /** [min, max] between the two lines — drives the shaded area. Null without a change series. */
  band: [number, number] | null;
};

const cumulativeByYear = (years: ContributionMarginYearBreakdown[]): number[] => {
  const totalsByYear = new Map(years.map((year) => [year.year, bandAvg(year.contributionMargin?.total)]));
  const maxYear = years.reduce((max, year) => Math.max(max, year.year), 0);

  const cumulative: number[] = [];
  let running = 0;
  for (let yearNum = 1; yearNum <= maxYear; yearNum += 1) {
    running += totalsByYear.get(yearNum) ?? 0;
    cumulative.push(Math.round(running));
  }
  return cumulative;
};

export const buildOnboardingForecastSeries = (
  baselineYears: ContributionMarginYearBreakdown[],
  changeYears: ContributionMarginYearBreakdown[] | null
): OnboardingForecastPoint[] => {
  const baselineCumulative = cumulativeByYear(baselineYears);
  const changeCumulative = changeYears?.length ? cumulativeByYear(changeYears) : null;

  return baselineCumulative.map((baselineValue, index) => {
    const changeValue = changeCumulative?.[index] ?? null;
    return {
      yearNum: index + 1,
      baselineCumulative: baselineValue,
      changeCumulative: changeValue,
      band: changeValue != null ? [Math.min(baselineValue, changeValue), Math.max(baselineValue, changeValue)] : null,
    };
  });
};

const niceStep = (roughStep: number): number => {
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const residual = roughStep / magnitude;
  if (residual > 5) return 10 * magnitude;
  if (residual > 2.5) return 5 * magnitude;
  if (residual > 1.2) return 2 * magnitude;
  return magnitude;
};

// Skip a zero tick when the series stays well above zero ... Recharts' auto domain puts "0" on the first x label.
export const getForecastYAxisTicks = (data: OnboardingForecastPoint[]): number[] => {
  const values = data.flatMap((point) => [point.baselineCumulative, ...(point.changeCumulative != null ? [point.changeCumulative] : [])]);
  if (values.length === 0) return [];

  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const range = dataMax - dataMin;
  const step = niceStep(Math.max(range > 0 ? range / 4 : Math.abs(dataMax) / 4, 1));

  let firstTick = Math.floor(dataMin / step) * step;
  if (firstTick <= 0 && dataMin > 0) firstTick += step;

  const ticks: number[] = [];
  for (let tick = firstTick; tick < dataMax + step && ticks.length < 8; tick += step) {
    ticks.push(tick);
  }
  return ticks;
};

// Re-space design ticks when the API horizon differs from what the layout was drawn for.
export const getForecastYearTicks = (yearCount: number, designTicks: number[]): number[] => {
  if (yearCount <= 0) return [];
  if (designTicks[designTicks.length - 1] === yearCount) return designTicks;
  if (yearCount <= designTicks.length) return Array.from({ length: yearCount }, (_, index) => index + 1);

  const step = (yearCount - 1) / (designTicks.length - 1);
  return [...new Set(designTicks.map((_, index) => Math.round(1 + index * step)))];
};

// Floor at Y=0 (or below for negative data) so a data-driven "0" tick never collides with the first x label.
export const getForecastYDomain = (data: OnboardingForecastPoint[], yTicks: number[]): [number, number] => {
  const dataFloor = Math.min(...data.map((point) => Math.min(point.baselineCumulative, point.changeCumulative ?? point.baselineCumulative)));

  return [Math.min(0, dataFloor, yTicks[0] ?? 0), yTicks[yTicks.length - 1] ?? 0];
};

/** Cumulative contribution margin at the end of the projection, on the api's avg band — the single figure both onboarding steps show. */
export const getCumulativeMarginTotal = (years: ContributionMarginYearBreakdown[] | undefined): number | null => {
  const cumulative = cumulativeByYear(years ?? []);
  // Empty rather than zero: a series that yields nothing must read "—", not a confident "about €0/ha".
  return cumulative.length > 0 ? cumulative[cumulative.length - 1]! : null;
};
